import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Critical pages - loaded immediately
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ProfileSettings from './pages/ProfileSettings';
import ForgotPassword from './components/auth/ForgotPassword';

// Global Navigation Component
import GlobalNav from './components/shared/GlobalNav';

// Lazy loaded pages - loaded on demand for faster initial load
const CustomerDashboard = lazy(() => import('./pages/CustomerDashboard'));
const OperatorDashboard = lazy(() => import('./pages/OperatorDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const RegionalManagerDashboard = lazy(() => import('./pages/RegionalManagerDashboard'));
const HelipadOwnerDashboard = lazy(() => import('./pages/HelipadOwnerDashboard'));
const VerticalOwnerDashboard = lazy(() => import('./pages/VerticalOwnerDashboard'));
const BookingPage = lazy(() => import('./pages/BookingPage'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const MarketplaceResults = lazy(() => import('./pages/MarketplaceResults'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
// New Role-based Dashboards - lazy loaded
const HRDashboard = lazy(() => import('./pages/HRDashboard'));
const EmployeePortal = lazy(() => import('./pages/EmployeePortal'));
const SalesDashboard = lazy(() => import('./pages/SalesDashboard'));
const SupportDashboard = lazy(() => import('./pages/SupportDashboard'));
const FinanceDashboard = lazy(() => import('./pages/FinanceDashboard'));
// Phase 1: Premium Services - lazy loaded
const MembershipPage = lazy(() => import('./pages/MembershipPage'));
const AviationExchangePage = lazy(() => import('./pages/AviationExchangePage'));
const ListingDetailPage = lazy(() => import('./pages/ListingDetailPage'));
const CorporateDashboard = lazy(() => import('./pages/CorporateDashboard'));
const DocumentVault = lazy(() => import('./pages/DocumentVault'));
const PaymentLinkPage = lazy(() => import('./pages/PaymentLinkPage'));
// Pilot Mobile Portal (PWA)
const PilotMobilePortal = lazy(() => import('./components/pilot/PilotMobilePortal'));
// Services & About Pages
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
// Fleet & Blog Pages
const FleetGalleryPage = lazy(() => import('./pages/FleetGalleryPage'));
const { BlogListPage, BlogPostPage } = require('./pages/BlogPage');
// Legal Pages
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
// 404 Page
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// Command Center & AI Pricing
const CommandCenterDashboard = lazy(() => import('./components/admin/CommandCenterDashboard'));
const AIPricingAdvisor = lazy(() => import('./components/admin/AIPricingAdvisor'));
// Account Unlock Page
const UnlockAccountPage = lazy(() => import('./pages/UnlockAccountPage'));
// Public Aircraft Browse with Compare
const PublicAircraftBrowse = lazy(() => import('./components/aircraft/PublicAircraftBrowse'));

// Import shared components
import AIChatbot from './components/shared/AIChatbot';
import KeyboardShortcutsPanel from './components/shared/KeyboardShortcutsPanel';
import OfflineIndicator from './components/shared/OfflineIndicator';
import { ThemeProvider, ThemeToggle } from './components/shared/ThemeToggle';

// Import Google Auth components
import { GoogleAuthSuccess, GoogleAuthError, EmergentAuthCallback } from './components/auth/GoogleLogin';

// Lazy load customer components
const InquiryStatus = lazy(() => import('./components/customer/InquiryStatus'));

// Loading spinner component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mx-auto mb-4"></div>
      <p className="text-white">Loading...</p>
    </div>
  </div>
);

function App() {
  const [user, setUser] = useState(() => {
    // Initialize user from localStorage synchronously
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  });
  const [loading, setLoading] = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
    <BrowserRouter>
      {/* Offline Indicator - Shows when network is disconnected */}
      <OfflineIndicator />
      
      {/* Global Navigation - Home Button */}
      <GlobalNav user={user} />
      
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage user={user} />} />
          <Route path="/login" element={<LoginPage setUser={setUser} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/profile" element={user ? <ProfileSettings user={user} setUser={setUser} /> : <LoginPage setUser={setUser} />} />
          
          {/* Google Auth Callback Routes */}
          <Route path="/auth/google/success" element={<GoogleAuthSuccess onLogin={setUser} />} />
          <Route path="/auth/google/error" element={<GoogleAuthError />} />
          <Route path="/auth/google/callback" element={<EmergentAuthCallback onLogin={setUser} />} />
          
          {/* Public Payment Link Page (no auth required) */}
          <Route path="/pay/:token" element={<PaymentLinkPage />} />
          
          {/* Public Pages - Services & About */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/fleet" element={<FleetGalleryPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/contact" element={<LandingPage />} />
          
          {/* Dynamic Legal Pages - Fetches from Backend API */}
          <Route path="/legal/terms" element={<LegalPage />} />
          <Route path="/legal/privacy" element={<LegalPage />} />
          <Route path="/legal/cancellation" element={<LegalPage />} />
          <Route path="/legal/:type" element={<LegalPage />} />
          
          {/* Public Aircraft Browse with Compare Feature */}
          <Route path="/aircraft/browse" element={<PublicAircraftBrowse />} />
          
          <Route
            path="/customer/marine"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/trips"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/refer"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/loyalty"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/profile"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/messages"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/listings"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/investments"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/watchlist"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/payments"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/stats"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/price-trends"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/route-suggestions"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/kyc"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/security"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/compare"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/complaints"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/refunds"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/auctions"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/smart-booking"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer/carbon"
            element={user && user.roles.includes('customer') ? <CustomerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
        <Route
          path="/customer/inquiry/:inquiryId"
          element={user && user.roles.includes('customer') ? <InquiryStatus user={user} /> : <Navigate to="/login" />}
        />
        <Route
          path="/customer/payment/:inquiryId"
          element={user && user.roles.includes('customer') ? <PaymentPage user={user} /> : <Navigate to="/login" />}
        />
        <Route
          path="/marketplace/results"
          element={user && user.roles.includes('customer') ? <MarketplaceResults user={user} /> : <Navigate to="/login" />}
        />
        <Route
          path="/operator/*"
          element={user && user.roles.includes('operator') ? <OperatorDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/admin/*"
          element={user && (user.roles.includes('admin') || user.roles.includes('super_admin') || user.roles.includes('ceo')) ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/regional/*"
          element={user && (user.roles.includes('regional_manager') || user.roles.includes('admin')) ? <RegionalManagerDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/yacht-owner/*"
          element={user && (user.roles.includes('yacht_owner') || user.roles.includes('admin')) ? <VerticalOwnerDashboard vertical="yacht" user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/cruise-operator/*"
          element={user && (user.roles.includes('cruise_operator') || user.roles.includes('admin')) ? <VerticalOwnerDashboard vertical="cruise" user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/helipad-owner/*"
          element={user && (user.roles.includes('helipad_owner') || user.roles.includes('admin')) ? <HelipadOwnerDashboard user={user} setUser={setUser} /> : <Navigate to="/login" />}
        />
        {/* New Role-based Dashboards */}
        <Route
          path="/employee/*"
          element={user && (user.roles.includes('employee') || user.roles.includes('hr') || user.roles.includes('sales') || user.roles.includes('finance') || user.roles.includes('support') || user.roles.includes('marketing') || user.roles.includes('operations') || user.roles.includes('admin')) ? <EmployeePortal user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/hr/*"
          element={user && (user.roles.includes('hr') || user.roles.includes('admin')) ? <HRDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/sales/*"
          element={user && (user.roles.includes('sales') || user.roles.includes('marketing') || user.roles.includes('admin')) ? <SalesDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/support/*"
          element={user && (user.roles.includes('support') || user.roles.includes('admin')) ? <SupportDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route
          path="/finance/*"
          element={user && (user.roles.includes('finance') || user.roles.includes('cfo') || user.roles.includes('finance_head') || user.roles.includes('accounts_manager') || user.roles.includes('treasury_analyst') || user.roles.includes('admin')) ? <FinanceDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
        />
        <Route path="/booking" element={<BookingPage user={user} />} />
        {/* Phase 1: Premium Services Routes */}
        <Route path="/membership" element={<MembershipPage user={user} />} />
        <Route path="/exchange" element={<AviationExchangePage user={user} />} />
        <Route path="/exchange/listing/:listingId" element={<ListingDetailPage user={user} />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route 
          path="/corporate/*" 
          element={user ? <CorporateDashboard user={user} /> : <Navigate to="/login" />} 
        />
        <Route 
          path="/vault" 
          element={user ? <DocumentVault user={user} /> : <Navigate to="/login" />} 
        />
        {/* Pilot Mobile Portal (PWA) - Accessible by pilots and admins */}
        <Route 
          path="/pilot-portal" 
          element={user && (user.roles.includes('pilot') || user.roles.includes('admin') || user.roles.includes('operator')) ? <PilotMobilePortal user={user} /> : <Navigate to="/login" />} 
        />
        
        {/* Command Center - Admin Only */}
        <Route 
          path="/command-center" 
          element={user && (user.roles.includes('admin') || user.roles.includes('super_admin')) ? <CommandCenterDashboard /> : <Navigate to="/login" />} 
        />
        
        {/* AI Pricing Advisor - Admin & Operators */}
        <Route 
          path="/ai-pricing" 
          element={user && (user.roles.includes('admin') || user.roles.includes('operator') || user.roles.includes('sales')) ? <AIPricingAdvisor /> : <Navigate to="/login" />} 
        />
        
        {/* Account Unlock Page - Public (accessed via email link) */}
        <Route path="/unlock-account" element={<UnlockAccountPage />} />
        
        {/* 404 Catch-all Route */}
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      
      {/* AI Chatbot - Available on all pages */}
      <AIChatbot user={user} />
      
      {/* Keyboard Shortcuts Panel - Press ? to open */}
      <KeyboardShortcutsPanel />
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;